import { useState } from "react";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Tag, X } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import { useAuth } from "@/context/AuthContext";

import {
  REGISTER_CREATE_ORDER,
  VERIFY_PAYMENT_API,
  GST_DETAILS,
  APPLY_COUPON,
} from "../context/Api";

const GSTIN_REGEX =
  /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

const RegistrationDialog = ({
  open,
  onOpenChange,
  region,
  priceDisplay,
  // Multi-exam: which certification is being bought (landing page = flagship HCASP)
  examCode = "HCASP",
  examName = "",
}) => {
  const { user } = useAuth();

  const [submitting, setSubmitting] = useState(false);

  // GST states
  const [hasGst, setHasGst] = useState(false);
  const [gstNumber, setGstNumber] = useState("");
  const [gstError, setGstError] = useState("");

  // Coupon states
  const [hasCoupon, setHasCoupon] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [couponError, setCouponError] = useState("");
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState("");

  const resetState = () => {
    setSubmitting(false);
    setHasGst(false);
    setGstNumber("");
    setGstError("");
    setHasCoupon(false);
    setCouponCode("");
    setCouponError("");
    setApplyingCoupon(false);
    setAppliedCoupon("");
  };

  // Dialog close hote hi state clear
  const handleOpenChange = (v) => {
    if (!v) {
      resetState();
    }

    onOpenChange(v);
  };

  // ================================
  // APPLY COUPON
  // ================================
  const applyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError("Please enter a coupon code.");
      toast.error("Please enter a coupon code.");
      return;
    }

    setCouponError("");
    setApplyingCoupon(true);

    try {
      const res = await APPLY_COUPON({
        coupon: couponCode.trim(),
        region,
        currency: region === "india" ? "INR" : "USD",
        exam_code: examCode,
      });

      /*
        API response:

        {
          "message": "Payment Successful",
          "amount": 0.0,
          "status": true
        }

        Axios wrapper ke according dono cases handle:
        res.amount
        ya
        res.data.amount
      */

      const couponResponse = res?.data ?? res;

      const couponAmount = couponResponse?.amount;

      if (
        couponResponse?.status === false ||
        couponAmount === undefined ||
        couponAmount === null
      ) {
        throw new Error(
          couponResponse?.message || "Invalid or expired coupon code."
        );
      }

      // API amount save karo
      setAppliedCoupon({
        code: couponCode.trim(),
        discount: couponResponse?.discount ?? 0,
        final_amount: couponAmount,
        message: couponResponse?.message,
      });

      toast.success(
        couponResponse?.message || "Coupon applied successfully."
      );
    } catch (err) {
      setAppliedCoupon("");

      const responseData = err?.response?.data;

      const msg =
        responseData?.message ||
        responseData?.error ||
        err?.message ||
        "Invalid or expired coupon code.";

      setCouponError(msg);
      toast.error(msg);
    } finally {
      setApplyingCoupon(false);
    }
  };

  // Coupon remove karne ke liye
  const removeCoupon = () => {
    setAppliedCoupon("");
    setCouponCode("");
    setCouponError("");
  };

  // ================================
  // PAY NOW
  // ================================
  const payNow = async () => {
    // GST checked hai but number nahi dala
    if (hasGst && !gstNumber.trim()) {
      setGstError("Please enter your GST number.");
      toast.error("Please enter your GST number.");
      return;
    }

    // GST number format galat hai
    if (hasGst && !GSTIN_REGEX.test(gstNumber.trim())) {
      setGstError("Please enter a valid GSTIN.");
      toast.error("Please enter a valid GST number.");
      return;
    }

    // Coupon checked hai but apply nahi kiya
    if (hasCoupon && !appliedCoupon) {
      setCouponError("Please apply your coupon code before proceeding.");
      toast.error("Please apply your coupon code before proceeding.");
      return;
    }

    setGstError("");
    setSubmitting(true);

    try {
      if (hasGst) {
        await GST_DETAILS({
          gst_number: gstNumber.trim(),
        });
      }

      const order = await REGISTER_CREATE_ORDER({
        name: user?.name,
        email: user?.email,
        phone: user?.phone,
        organization: user?.organization,
        designation: user?.designation,
        city: user?.city,
        state: user?.state,
        country: user?.country,
        region,
        currency: region === "india" ? "INR" : "USD",

        // GST number
        gst_number: hasGst ? gstNumber.trim() : null,

        // Coupon code
        coupon: appliedCoupon ? appliedCoupon.code : "",

        // Certification being purchased
        exam_code: examCode,
      });

      const options = {
        key: order.key,
        amount: order.amount,
        currency: order.currency,
        order_id: order.order_id,

        name: order.exam_code || examCode,
        description: `${order.exam_code || examCode} Registration`,

        prefill: {
          name: user?.name,
          email: user?.email,
          contact: user?.phone,
        },

        theme: {
          color: "#1976d2",
        },

        handler: async function (response) {
          try {
            await VERIFY_PAYMENT_API({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });

            window.location.href = "/user-panel";
          } catch (err) {
            toast.error(
              err?.response?.data?.error ||
              "Payment verification failed."
            );
          }
        },

        modal: {
          ondismiss: function () {
            toast.error("Payment cancelled.");
          },
        },
      };

      onOpenChange(false);

      setTimeout(() => {
        const razorpay = new window.Razorpay(options);
        razorpay.open();
      }, 300);
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
        error?.response?.data?.error ||
        error?.error ||
        error?.message ||
        "Unable to process registration."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const originalAmount = priceDisplay;

  const finalAmount =
    appliedCoupon?.final_amount !== undefined &&
      appliedCoupon?.final_amount !== null
      ? appliedCoupon.final_amount
      : priceDisplay;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid="registration-dialog"
        className="max-w-md bg-[#0a0a12] border-[#3d414f] text-white"
      >
        <DialogHeader>
          <DialogTitle className="font-display font-700 text-2xl text-white">
            Confirm your registration
          </DialogTitle>
        </DialogHeader>

        {/* =====================================
            MAIN CONTENT
        ====================================== */}
        <div className="py-6 flex flex-col items-center text-center">
          {/* Shield Icon */}
          <ShieldCheck
            size={48}
            className="text-[#16fcfb]"
          />

          {/* Registration Details */}
          <p className="mt-5 font-body text-sm text-white/70 max-w-sm">
            You're registering as{" "}
            <span className="text-[#16fcfb]">
              {user?.name}
            </span>{" "}
            ({user?.email}){examName ? <> for <span className="text-white">{examName}</span></> : null} on the{" "}
            {region === "india"
              ? "India"
              : "International"}{" "}
            plan
            {priceDisplay ? (
              <>
                {" "}
                at{" "}
                {appliedCoupon ? (
                  <span className="text-white">
                    <span className="mr-2 text-white/50 line-through">
                      {originalAmount}
                    </span>

                    <span className="font-semibold text-[#16fcfb]">
                      {finalAmount}
                    </span>
                  </span>
                ) : (
                  <span className="text-white">
                    {priceDisplay}
                  </span>
                )}
              </>
            ) : null}
          </p>

          {/* =====================================
              GST CHECKBOX
          ====================================== */}
          <div className="w-full mt-6 text-left">
            <label className="flex items-center gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={hasGst}
                onChange={(e) => {
                  const checked = e.target.checked;

                  setHasGst(checked);

                  if (!checked) {
                    setGstNumber("");
                    setGstError("");
                  }
                }}
                className="h-4 w-4 accent-[#16fcfb] cursor-pointer"
              />

              <span className="text-sm text-white/80">
                I have a GST number
              </span>
            </label>
          </div>

          {/* =====================================
              GST NUMBER FIELD
          ====================================== */}
          {hasGst && (
            <div className="w-full mt-4 text-left">
              <label className="text-sm text-white/80">
                GST Number
              </label>

              <Input
                value={gstNumber}
                onChange={(e) => {
                  const value = e.target.value.toUpperCase();

                  setGstNumber(value);

                  if (!value.trim()) {
                    setGstError("");
                  } else if (!GSTIN_REGEX.test(value.trim())) {
                    setGstError("Please enter a valid GSTIN.");
                  } else {
                    setGstError("");
                  }
                }}
                placeholder="Enter GST Number"
                maxLength={15}
                className={`mt-2 bg-[#11111a] border-[#3d414f] text-white placeholder:text-white/40 ${gstError ? "border-red-500" : ""
                  }`}
              />

              {gstError ? (
                <p className="mt-1 text-xs text-red-400">
                  {gstError}
                </p>
              ) : (
                <p className="mt-1 text-xs text-white/40">
                  Enter your 15-character GST number.
                </p>
              )}
            </div>
          )}

          {/* =====================================
              COUPON CHECKBOX
          ====================================== */}
          <div className="w-full mt-4 text-left">
            <label className="flex items-center gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={hasCoupon}
                onChange={(e) => {
                  const checked = e.target.checked;

                  setHasCoupon(checked);

                  if (!checked) {
                    removeCoupon();
                  }
                }}
                className="h-4 w-4 accent-[#16fcfb] cursor-pointer"
              />

              <span className="text-sm text-white/80">
                I have a coupon code
              </span>
            </label>
          </div>

          {/* =====================================
              COUPON CODE FIELD
          ====================================== */}
          {hasCoupon && (
            <div className="w-full mt-4 text-left">
              <label className="text-sm text-white/80">
                Coupon Code
              </label>

              {appliedCoupon ? (
                // Coupon apply ho chuka hai
                <div className="mt-2 flex items-center justify-between rounded-md border border-[#16fcfb]/40 bg-[#16fcfb]/10 px-3 py-2">
                  <div className="flex items-center gap-2 text-sm text-[#16fcfb]">
                    <Tag size={14} />
                    <span>
                      {appliedCoupon.code} applied
                    </span>
                  </div>

                  <button
                    type="button"
                    onClick={removeCoupon}
                    className="text-white/50 hover:text-white"
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <div className="mt-2 flex items-center gap-2">
                  <Input
                    value={couponCode}
                    onChange={(e) => {
                      const value =
                        e.target.value.toUpperCase();

                      setCouponCode(value);

                      if (couponError) {
                        setCouponError("");
                      }
                    }}
                    placeholder="Enter Coupon Code"
                    disabled={applyingCoupon}
                    className={`bg-[#11111a] border-[#3d414f] text-white placeholder:text-white/40 ${couponError ? "border-red-500" : ""
                      }`}
                  />

                  <Button
                    type="button"
                    onClick={applyCoupon}
                    disabled={applyingCoupon}
                    className="shrink-0 rounded-md h-10 px-4 bg-[#16fcfb] text-[#07070c] hover:bg-[#97b8ff] font-mono2 text-xs uppercase tracking-widest"
                  >
                    {applyingCoupon ? (
                      <Loader2
                        size={14}
                        className="animate-spin"
                      />
                    ) : (
                      "Apply"
                    )}
                  </Button>
                </div>
              )}

              {couponError ? (
                <p className="mt-1 text-xs text-red-400">
                  {couponError}
                </p>
              ) : (
                !appliedCoupon && (
                  <p className="mt-1 text-xs text-white/40">
                    Enter a valid coupon code to get a discount.
                  </p>
                )
              )}
            </div>
          )}

          {/* =====================================
              PAY BUTTON
          ====================================== */}
          <Button
            data-testid="reg-pay-btn"
            onClick={payNow}
            disabled={submitting || applyingCoupon}
            className="mt-8 w-full rounded-full h-12 bg-[#16fcfb] text-[#07070c] hover:bg-[#97b8ff] font-mono2 text-xs uppercase tracking-widest glow-cyan"
          >
            {submitting ? (
              <>
                <Loader2
                  size={16}
                  className="mr-2 animate-spin"
                />
                Processing
              </>
            ) : applyingCoupon ? (
              <>
                <Loader2
                  size={16}
                  className="mr-2 animate-spin"
                />
                Applying Coupon
              </>
            ) : (
              "Proceed to Payment"
            )}
          </Button>
        </div>

        {/* =====================================
            COUPON API LOADER OVERLAY
        ====================================== */}
        {applyingCoupon && (
          <div className="absolute inset-0 z-50 flex items-center justify-center rounded-lg bg-black/70 backdrop-blur-[2px]">
            <div className="flex flex-col items-center gap-3">
              <Loader2
                size={36}
                className="animate-spin text-[#16fcfb]"
              />

              <p className="text-sm text-white">
                Applying coupon...
              </p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RegistrationDialog;